import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { UPDATE_CREDIT_APPLICATION_V2_QUERY, UPSERT_TASK } from '../queries';
import { ContactPayloadInput } from 'modules/creditApplicationDetailsActions/types';
import { CreditApplication } from '@trnsact/trnsact-shared-types';
import {
  getNewSignorAndGuarantorSignatureTaskForContact,
  getSignatureTaskForContact,
} from 'services/creditAppModalService';
import _ from 'lodash';

export function useManageContact() {
  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const [updateCreditAppRequest] = useMutation(UPDATE_CREDIT_APPLICATION_V2_QUERY, {
    context: { authRequired: true },
  });

  const [upsertTask] = useMutation(UPSERT_TASK, {
    context: { authRequired: true },
  });

  const hanldeSavingContact = async (
    contactToSave: ContactPayloadInput | null,
    creditApplication: CreditApplication
  ) => {
    if (!contactToSave) {
      return;
    }
    await updateCreditAppRequest({
      variables: {
        creditAppId: creditApplication.id,
        input: {
          owners: [contactToSave],
        },
      },
    });
  };

  const handleSaveContactWithTaskCreation = async (
    contactToSave: ContactPayloadInput,
    creditApplication: CreditApplication,
    vendorOpportunityId: string,
    refetchCreditApp: () => any
  ) => {
    await hanldeSavingContact(contactToSave, creditApplication);
    const refetchedCreditAppData = await refetchCreditApp();
    const refetchedCreditApp = refetchedCreditAppData.data.creditApplication;
    const allAvailableContacts = [
      ...[refetchedCreditApp.primaryContact],
      ...refetchedCreditApp.creditApplicationOwner,
      ...refetchedCreditApp.creditApplicationContacts,
    ];
    const savedContact = _.find(allAvailableContacts, {
      email: contactToSave?.email,
      ownerNumber: contactToSave?.ownerNumber,
    });
    const getTaskFnToUse = savedContact.pg
      ? getNewSignorAndGuarantorSignatureTaskForContact
      : getSignatureTaskForContact;
    const taskToCreate = getTaskFnToUse(
      savedContact,
      vendorOpportunityId,
      creditApplication.dynamicsId,
      account,
      userProfile
    );
    await upsertTask({
      variables: {
        input: taskToCreate,
      },
    });
  };

  const includeContactInCreditApplication = async (creditApplicationId: string, contact: any) => {
    try {
      const ownerPropsToUpdate: any = {
        ownerPgId: contact.ownerPgId,
        isContactOnly: false,
      };
      await updateCreditAppRequest({
        variables: {
          creditAppId: creditApplicationId,
          input: {
            owners: [ownerPropsToUpdate],
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const removeContactFromCreditApplication = async (creditApplicationId: string, contact: any) => {
    try {
      const ownerPropsToUpdate: any = {
        ownerPgId: contact.ownerPgId,
        isContactOnly: true,
      };
      if (contact.pc) {
        // convert primary contact to pc only
        ownerPropsToUpdate.isOwnerPc = true;
        ownerPropsToUpdate.isOwnerPcOnly = true;
      }
      await updateCreditAppRequest({
        variables: {
          creditAppId: creditApplicationId,
          input: {
            owners: [ownerPropsToUpdate],
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    hanldeSavingContact,
    handleSaveContactWithTaskCreation,
    removeContactFromCreditApplication,
    includeContactInCreditApplication,
  };
}
